import { computed, unref, type MaybeRef } from 'vue';

import {
  useFileAiPromptsQuery,
  type FileAiPromptFragmentFragment,
  type FileAiPromptsQueryVariables,
  type FileAiPromptSearchInput,
  type GenericCompleteDraftEnum,
  type FileAiPromptTypeEnum,
} from '@/graphql/generated/graphql';
import useTableSearchOrder from '@/ui/Table/useTableRouter/seachOrder/useTableSearchOrder';
import getByKeys from '@/util/search/getKey';
import { getIntegerOrUndefined } from '~/util/numbers/getIntegerOrDefault';

const useFileAiPromptsSearch = (
  setURL?: boolean,
  forceVariables?: MaybeRef<FileAiPromptsQueryVariables | undefined>,
) => {
  const {
    query, setQuery, search, setSearch,
  } = useTableSearchOrder<keyof FileAiPromptFragmentFragment, Partial<FileAiPromptSearchInput>>(setURL);

  const variables = computed<FileAiPromptsQueryVariables>(() => {
    const { search: querySearch, ...other } = query.value;
    const forced = unref(forceVariables);
    const vars: FileAiPromptsQueryVariables = {
      ...other,
      ...forced,
    };
    const searchInput = querySearch
      ? getByKeys<keyof FileAiPromptSearchInput>(
        ['id', 'type', 'version', 'status'],
        querySearch,
        'type',
      )
      : undefined;
    vars.input = {
      ...forced?.input,
      ...searchInput,
      id: searchInput?.id ? getIntegerOrUndefined(searchInput.id) : undefined,
      status: searchInput?.status ? searchInput.status as GenericCompleteDraftEnum : undefined,
      type: searchInput?.type ? searchInput.type as FileAiPromptTypeEnum : undefined,
    };
    return vars;
  });

  const result = useFileAiPromptsQuery({ variables });
  const data = computed(() => result.data.value?.fileAiPrompts);

  return {
    data,
    fetching: result.fetching,
    error: result.error,
    refetch: () => result.executeQuery({ requestPolicy: 'network-only' }),
    query,
    setQuery,
    search,
    setSearch,
    variables,
  };
};

export default useFileAiPromptsSearch;
